/**
 * The wallet — coin balance plus an audited ledger.
 *
 * ── Why every credit needs a key ────────────────────────────────────────────
 *
 * `credit()` is IDEMPOTENT on an explicit key, and that is the whole design.
 * Rewards are handed out from UI code that re-renders freely, and this project
 * has already shipped that bug twice: honours counted a trophy per repaint, and
 * a season banked itself on every render until a token guard went in. A wallet
 * has the same shape of risk with worse consequences — silently minting
 * currency is unrecoverable once players have spent it.
 *
 * So a caller must name the event: `match:<seed>:<tick>`, `season:<club>:<n>`,
 * `wc:<seed>:final`. Same key twice, second one is dropped. Getting the key
 * right is the caller's one job, and it is a much easier job than remembering
 * which code paths can re-run.
 *
 * ── Why a ledger at all ─────────────────────────────────────────────────────
 *
 * A bare number cannot answer "where did my coins go", which is the single most
 * common support question any game with a currency receives. Each entry carries
 * the balance AFTER it, so an auditor reads the tail rather than replaying from
 * zero, and a corrupted middle entry does not poison everything after it.
 *
 * ── Trust ───────────────────────────────────────────────────────────────────
 *
 * This is localStorage: a determined player can edit it. That is ACCEPTABLE for
 * an earned soft currency in a single-player context and NOT acceptable for
 * anything bought with money, which is why entitlements live behind their own
 * server-shaped interface (types.ts). When a backend arrives the wallet becomes
 * a cache of a server balance and this file's API does not change.
 */

import type { Coins, LedgerEntry, LedgerReason, WalletState } from './types.js';

export const WALLET_KEY = 'fg.wallet.v1';

/**
 * Ledger entries kept. Enough to explain a session and answer "where did my
 * coins go", not so many that the record outgrows its own storage quota.
 */
const LEDGER_MAX = 120;

/**
 * Starting balance — enough for EXACTLY ONE common celebration (400).
 *
 * Set from 250 after opening the store on a fresh profile and finding every
 * single row greyed out reading "150 short". A shop where a new player can
 * afford nothing reads as a paywall on first contact, and first contact is the
 * only one some players give you. 500 turns that screen into a choice — pick
 * your first celebration — and still leaves the whole catalogue to earn.
 */
export const STARTING_COINS = 500;

export function defaultWallet(): WalletState {
  return { version: 1, balance: STARTING_COINS, ledger: [], lifetimeEarned: 0 };
}

export function loadWallet(): WalletState {
  try {
    const raw = localStorage.getItem(WALLET_KEY);
    if (!raw) return defaultWallet();
    const parsed = JSON.parse(raw) as Partial<WalletState>;
    // Merge over defaults so a wallet written by an older build, missing a
    // field added since, loads rather than resetting a player to zero.
    return {
      version: 1,
      balance: Math.max(0, Math.floor(parsed.balance ?? STARTING_COINS)),
      ledger: Array.isArray(parsed.ledger) ? parsed.ledger.slice(-LEDGER_MAX) : [],
      lifetimeEarned: Math.max(0, Math.floor(parsed.lifetimeEarned ?? 0)),
    };
  } catch {
    // A corrupt wallet must not brick the menu. Start clean rather than throw.
    return defaultWallet();
  }
}

export function saveWallet(w: WalletState): void {
  try {
    localStorage.setItem(WALLET_KEY, JSON.stringify(w));
  } catch {
    /* quota or private mode — the balance is a nicety, never a hard failure */
  }
}

export function balance(): Coins {
  return loadWallet().balance;
}

/** True when this exact event has already moved the balance. */
export function hasEntry(w: WalletState, key: string): boolean {
  return w.ledger.some((e) => e.key === key);
}

/**
 * Apply a signed movement. Pure — takes a state, returns a state — so the rules
 * are testable without touching storage.
 *
 * Returns the SAME state object when the key is a duplicate or the delta is
 * zero, which lets callers cheaply detect "nothing happened".
 */
export function applyEntry(
  w: WalletState,
  key: string,
  delta: Coins,
  reason: LedgerReason,
  note: string,
  now = Date.now(),
): WalletState {
  if (delta === 0 || hasEntry(w, key)) return w;
  // Never let a balance go negative: a debit larger than the balance is a bug
  // in the caller, and clamping is far kinder than a nonsensical wallet.
  const next = Math.max(0, w.balance + Math.floor(delta));
  const entry: LedgerEntry = {
    key,
    at: now,
    delta: Math.floor(delta),
    reason,
    note,
    balance: next,
  };
  return {
    version: 1,
    balance: next,
    ledger: [...w.ledger, entry].slice(-LEDGER_MAX),
    lifetimeEarned: w.lifetimeEarned + Math.max(0, Math.floor(delta)),
  };
}

/**
 * Award coins. Idempotent on `key` — see the header.
 * Returns the coins actually added (0 when it was a duplicate).
 */
export function credit(key: string, amount: Coins, reason: LedgerReason, note: string): Coins {
  if (amount <= 0) return 0;
  const before = loadWallet();
  const after = applyEntry(before, key, amount, reason, note);
  if (after === before) return 0;
  saveWallet(after);
  return after.balance - before.balance;
}

/**
 * Spend coins. Returns false and changes nothing when the balance is short —
 * the caller must not assume success, because "buy" is the one place where a
 * silent failure loses a player their item.
 */
export function debit(key: string, amount: Coins, note: string): boolean {
  if (amount <= 0) return true;
  const before = loadWallet();
  if (before.balance < amount) return false;
  if (hasEntry(before, key)) return false;
  saveWallet(applyEntry(before, key, -amount, 'purchase', note));
  return true;
}

/** Newest-first view, for a wallet screen. */
export function recentLedger(limit = 12): LedgerEntry[] {
  return loadWallet().ledger.slice(-limit).reverse();
}

export function resetWallet(): void {
  saveWallet(defaultWallet());
}
